import {
  Edit,
  Create,
  SimpleForm,
  EditButton,
  DeleteButton,
  required,
  useTranslate,
  useListContext,
  Toolbar,
  SaveButton,
  useRecordContext,
  useNotify,
  NumberInput,
  minValue,
  maxValue,
  ExportButton,
  FunctionField,
} from 'react-admin';
import jsonExport from 'jsonexport/dist';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import downloadCSVCompat from '../../common_modules/utils/downloadCSVCompat';
import useErrorHandler from '../../common_modules/hooks/useErrorHandler';
import { getLocalStorageId } from '../../authProvider';
import { Box, Typography, InputAdornment, IconButton, useMediaQuery, useTheme, Button } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import FormValidation from '../../common_modules/components/FormValidation';
import SignedPercentNumberInput from '../../common_modules/components/SignedPercentNumberInput';
import formatSignedPercent from '../../common_modules/utils/formatSignedPercent';
import FlexibleListLayout from '../../common_modules/components/FlexibleListLayout';
import { PersistentList } from '../../common_modules/components/PersistentList';

// ============================================================================
// 1. ヘルパー / ツールバー
// ============================================================================

const handleDuplicateError = (
  error: any,
  setError: (field: string, options: { type: string; message: string }) => void,
  notify: ReturnType<typeof useNotify>
) => {
  const status = error?.status || error?.body?.statusCode;
  if (status === 409 || error?.body?.errors?.usageCount) {
    setError('usageCount', {
      type: 'manual',
      message: '同一の注文回数が既に登録されています。',
    });
    return true;
  }
  if (error?.body?.errors) {
    for (const [field, message] of Object.entries(error.body.errors)) {
      setError(field, { type: 'manual', message: message as string });
    }
    return true;
  }
  notify('ra.notification.error', { type: 'error', message: error?.message || 'ra.message.error' });
  return false;
};

const ResetFormOnSuccessToolbar = () => {
  const notify = useNotify();
  const { reset, setError } = useFormContext();

  const onError = (error: any) => {
    handleDuplicateError(error, setError, notify);
  };

  const handleSuccess = () => {
    reset();
    notify('ra.notification.created', { type: 'info', messageArgs: { smart_count: 1 } });
  };

  return (
    <Toolbar>
      <SaveButton mutationOptions={{ onError, onSuccess: handleSuccess }} type="button" />
    </Toolbar>
  );
};

const CustomDeleteButton = () => {
  const record = useRecordContext();
  return (
    <Box sx={{ marginLeft: '16px' }}>
      <DeleteButton
        redirect={`/settings/${getLocalStorageId()}`}
        confirmTitle={`${record?.usageCount}回目の利用回数別ルールを削除`}
        confirmContent="本当に削除しますか？"
        mutationMode="pessimistic"
      />
    </Box>
  );
};

const CustomEditToolbar = ({ isFormValid }: { isFormValid: boolean }) => {
  const notify = useNotify();
  const { setError } = useFormContext();

  const onError = (error: any) => {
    handleDuplicateError(error, setError, notify);
  };

  return (
    <Toolbar sx={{ justifyContent: 'space-between', width: '100%', mt: 2 }}>
      <SaveButton disabled={!isFormValid} mutationOptions={{ onError }} type="button"/>
      <CustomDeleteButton />
    </Toolbar>
  );
};

// ============================================================================
// 2. リストアクション（タイトル + CSV + 作成ボタン）
// ============================================================================

export const UsageCountRewardRateListActions = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const createPath = '/usage-count-reward-rates/create';
  const createLabel = translate('resources.usage-count-reward-rates.actions.create');

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <Typography variant="h6" component="h2">
        注文回数による報酬率ルール設定
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ExportButton label="CSVダウンロード" icon={<DownloadIcon sx={{ fontSize: '20px', marginRight: '4px' }} />} />
        {isSmall ? (
          <IconButton color="primary" aria-label={createLabel} component="a" href={`#${createPath}`} size="medium">
            <AddIcon />
          </IconButton>
        ) : (
          <Button variant="text" color="primary" startIcon={<AddIcon />} component="a" href={`#${createPath}`}>
            {createLabel}
          </Button>
        )}
      </Box>
    </Box>
  );
};

// ============================================================================
// 3. 一覧テーブル
// ============================================================================

const UsageCountRewardRateListLayout = () => {
  const { total, isLoading } = useListContext();
  const translate = useTranslate();
  if (isLoading) return null;

  return (
    <>
      {total > 0 && (
        <FlexibleListLayout rowClick={false} bulkActionButtons={false}>
          <FunctionField
            source="usageCount"
            label={translate('resources.usage-count-reward-rates.fields.usageCount')}
            render={(record: any) => (record?.usageCount != null ? `${record.usageCount}回目` : '')}
          />
          <FunctionField
            source="rewardRateDelta"
            label={translate('resources.usage-count-reward-rates.fields.rewardRateDelta')}
            render={(record: any) => formatSignedPercent(record?.rewardRateDelta)}
          />
          <FunctionField
            label=" "
            render={() => (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <EditButton />
                <CustomDeleteButton />
              </Box>
            )}
          />
        </FlexibleListLayout>
      )}
      {total === 0 && (
        <Box textAlign="center" m={2}>
          <Typography variant="body2">{translate('ra.navigation.no_results')}</Typography>
        </Box>
      )}
    </>
  );
};

// ============================================================================
// 4. リスト本体（Settings.tsx に埋め込むセクション）
// ============================================================================

export const UsageCountRewardRateList = () => {
  const translate = useTranslate();

  const csvExporter = (data: any[]) => {
    const headerUsageCount = translate('resources.usage-count-reward-rates.fields.usageCount');
    const headerDelta = translate('resources.usage-count-reward-rates.fields.rewardRateDelta');
    const fileName = translate('resources.usage-count-reward-rates.name')
    const dataForExport = (data || [])
      .slice()
      .sort((a, b) => (a.usageCount ?? 0) - (b.usageCount ?? 0))
      .map((record) => ({
        [headerUsageCount]: record.usageCount,
        [headerDelta]: record.rewardRateDelta,
      }));
    // 0 件時もヘッダーのみの CSV を出力する（jsonExport の挙動上、ダミー行は不要）
    if (dataForExport.length === 0) {
      const csv = `${headerUsageCount},${headerDelta}\n`;
      const bom = '﻿';
      downloadCSVCompat(bom + csv, fileName);
      return;
    }
    jsonExport(dataForExport, (err, csv) => {
      if (err) {
        console.error(err);
        return;
      }
      const bom = '﻿';
      downloadCSVCompat(bom + csv, fileName);
    });
  };

  return (
    <PersistentList
      title=" "
      resource="usage-count-reward-rates"
      storeKey="usage-count-reward-rates"
      actions={<UsageCountRewardRateListActions />}
      sort={{ field: 'usageCount', order: 'ASC' }}
      sx={{ mt: 2 }}
      empty={false}
      exporter={csvExporter}
    >
      <UsageCountRewardRateListLayout />
    </PersistentList>
  );
};

// ============================================================================
// 5. Create / Edit フォーム
// ============================================================================

const UsageCountRewardRateFormFields = () => {
  return (
    <>
      <NumberInput
        source="usageCount"
        validate={[required(), minValue(1), maxValue(1000000)]}
        min={1}
        max={1000000}
        step={1}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (['+', '-', '.', 'e', 'E'].includes(event.key)) {
            event.preventDefault();
          }
        }}
        InputProps={{
          endAdornment: <InputAdornment position="end">回目</InputAdornment>,
        }}
      />
      <Typography variant="caption" color="textSecondary" display="block">
        ※ 1〜1,000,000 まで入力可能
      </Typography>
      <SignedPercentNumberInput
        source="rewardRateDelta"
        validate={[required(), minValue(-100), maxValue(500)]}
      />
      <Typography variant="caption" color="textSecondary" display="block">
        ※ -100.00〜500.00 まで入力可能
      </Typography>
      <Typography variant="caption" color="textSecondary" display="block">
        ※ 報酬率の減算を実施しても、最終的な報酬率は0%以下にはなりません。
      </Typography>
    </>
  );
};

const UsageCountRewardRateForm = () => {
  const record = useRecordContext();
  const isEdit = !!record?.id;
  const requiredFields = ['usageCount', 'rewardRateDelta'];

  return (
    <SimpleForm
      reValidateMode="onSubmit"
      toolbar={
        isEdit ? (
          <FormValidation requiredFields={requiredFields}>
            {(isValid) => <CustomEditToolbar isFormValid={isValid} />}
          </FormValidation>
        ) : (
          <ResetFormOnSuccessToolbar />
        )
      }
      sx={{ maxWidth: '600px' }}
    >
      <UsageCountRewardRateFormFields />
    </SimpleForm>
  );
};

export const UsageCountRewardRateCreate = () => {
  const handleError = useErrorHandler();
  const translate = useTranslate();
  const transform = async (data: any) => {
    const clientId = parseInt(getLocalStorageId()!, 10);
    return {
      ...data,
      clientId,
      rewardRateDelta:
        data.rewardRateDelta !== null && data.rewardRateDelta !== undefined ? String(data.rewardRateDelta) : data.rewardRateDelta,
    };
  };

  return (
    <Create
      title={translate('resources.usage-count-reward-rates.actions.create')}
      transform={transform}
      mutationOptions={{ onError: handleError }}
      resource="usage-count-reward-rates"
      redirect={`/settings/${getLocalStorageId()}`}
    >
      <UsageCountRewardRateForm />
    </Create>
  );
};

export const UsageCountRewardRateEdit = () => {
  const handleError = useErrorHandler();
  const translate = useTranslate();
  const transform = async (data: any) => ({
    ...data,
    rewardRateDelta:
      data.rewardRateDelta !== null && data.rewardRateDelta !== undefined ? String(data.rewardRateDelta) : data.rewardRateDelta,
  });

  return (
    <Edit
      title={translate('resources.usage-count-reward-rates.actions.edit')}
      redirect={`/settings/${getLocalStorageId()}`}
      mutationMode="pessimistic"
      transform={transform}
      mutationOptions={{ onError: handleError }}
    >
      <UsageCountRewardRateForm />
    </Edit>
  );
};
